/*
 * FirePits.Store Predictive Search V1.3
 * - Preserves Dawn predictive-search behavior
 * - Adds SKU-aware Shopify Predictive Search
 * - Requests separate result allowances per resource type
 * - Loads and caches Product Finder collection aliases once
 * - Merges, ranks, and deduplicates locally matched collections
 * - Adds a limited product-backed collection fallback when direct collection
 *   matches are sparse
 */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.predictiveResourceLimit = 8;
    this.queryResultLimit = 3;
    this.collectionResultLimit = 4;
    this.collectionFallbackTriggerCount = 2;
    this.collectionFallbackResultLimit = 2;
    this.pageResultLimit = 3;
    this.articleResultLimit = 2;
    this.collectionDataSectionId = 'predictive-search-collections-data';

    this.collectionIndexPromise = this.getCollectionIndex();
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getCacheKey(searchTerm) {
    return this.normalize(searchTerm);
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove results when they are no longer relevant to the new term.
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term immediately instead of waiting for the request to finish.
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // The term changed in another search input; treat it as a user change.
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving when navigating results with arrow keys.
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (!currentButtonText || !previousTerm) return;

    const previousTermMatches = previousTerm
      ? currentButtonText.match(new RegExp(this.escapeRegExp(previousTerm), 'g'))
      : null;

    if (previousTermMatches && previousTermMatches.length > 1) {
      // Avoid replacing text when the previous term appears outside the query.
      return;
    }

    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Exclude hidden duplicate page/article resources.
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let index = 0;

    while (selectedElementIndex === -1 && index < allVisibleElements.length) {
      if (allVisibleElements[index] === selectedElement) {
        selectedElementIndex = index;
      }
      index += 1;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getPredictiveSearchUrl(searchTerm) {
    const searchParams = new URLSearchParams();

    searchParams.set('q', searchTerm);
    searchParams.set('section_id', 'predictive-search');
    searchParams.set(
      'resources[type]',
      'query,product,collection,page,article'
    );
    searchParams.set('resources[limit]', String(this.predictiveResourceLimit));
    searchParams.set('resources[limit_scope]', 'each');
    searchParams.set('resources[options][unavailable_products]', 'last');
    searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,vendor'
    );

    return `${routes.predictive_search_url}?${searchParams.toString()}`;
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getCacheKey(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Cancel an older predictive request before beginning the next one.
    this.abortController.abort();
    this.abortController = new AbortController();

    const predictiveRequest = fetch(this.getPredictiveSearchUrl(searchTerm), {
      signal: this.abortController.signal,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Predictive Search request failed with status ${response.status}`);
      }

      return response.text();
    });

    Promise.all([predictiveRequest, this.collectionIndexPromise])
      .then(([text, collectionIndex]) => {
        const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
        const section = parsedDocument.querySelector('#shopify-section-predictive-search');

        if (!section) {
          throw new Error('Predictive Search section markup was not returned.');
        }

        const resultsMarkup = this.prepareSearchResultsMarkup(
          section.innerHTML,
          searchTerm,
          collectionIndex
        );

        // Keep the cache synchronized across all predictive-search instances.
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }

        this.close();
        console.error(error);
      });
  }

  getCollectionIndex() {
    if (window.__firePitsPredictiveCollectionIndexPromise) {
      return window.__firePitsPredictiveCollectionIndexPromise;
    }

    const routesRoot =
      window.Shopify?.routes?.root ||
      window.routes?.root_url ||
      '/';

    const sectionUrl = new URL(routesRoot, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.collectionDataSectionId);

    window.__firePitsPredictiveCollectionIndexPromise = fetch(sectionUrl.toString(), {
      headers: {
        Accept: 'text/html',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Collection alias section request failed with status ${response.status}`
          );
        }

        return response.text();
      })
      .then((text) => {
        const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
        const jsonElement = parsedDocument.querySelector(
          '[data-predictive-search-collections-json]'
        );

        if (!jsonElement) {
          throw new Error(
            `Section "${this.collectionDataSectionId}" did not return its collection JSON index.`
          );
        }

        const collectionIndex = JSON.parse(jsonElement.textContent || '[]');

        if (!Array.isArray(collectionIndex)) {
          throw new Error('The collection alias index is not a JSON array.');
        }

        return collectionIndex;
      })
      .catch((error) => {
        // Native Shopify predictive search should keep working if the
        // supplemental alias index is unavailable.
        console.warn('Predictive Search collection aliases were not loaded.', error);
        return [];
      });

    return window.__firePitsPredictiveCollectionIndexPromise;
  }

  prepareSearchResultsMarkup(resultsMarkup, searchTerm, collectionIndex) {
    const parsedDocument = new DOMParser().parseFromString(
      `<div data-predictive-search-fragment>${resultsMarkup}</div>`,
      'text/html'
    );
    const fragment = parsedDocument.querySelector('[data-predictive-search-fragment]');
    const resultsRoot = fragment?.querySelector('#predictive-search-results');

    if (!fragment || !resultsRoot) {
      return resultsMarkup;
    }

    const localMatches = this.getLocalCollectionMatches(
      collectionIndex,
      searchTerm
    );

    this.trimNativeResults(resultsRoot);
    this.mergeCollectionResults(
      resultsRoot,
      localMatches,
      searchTerm,
      collectionIndex
    );
    this.updateLiveRegionCount(resultsRoot);

    return fragment.innerHTML;
  }

  trimNativeResults(resultsRoot) {
    const trimItems = (selector, limit) => {
      Array.from(resultsRoot.querySelectorAll(selector)).forEach(
        (item, index) => {
          if (index >= limit) item.remove();
        }
      );
    };

    trimItems(
      'li[id^="predictive-search-option-query-"]',
      this.queryResultLimit
    );
    trimItems(
      'li[id^="predictive-search-option-page-desktop-"]',
      this.pageResultLimit
    );
    trimItems(
      'li[id^="predictive-search-option-page-mobile-"]',
      this.pageResultLimit
    );
    trimItems(
      'li[id^="predictive-search-option-article-desktop-"]',
      this.articleResultLimit
    );
    trimItems(
      'li[id^="predictive-search-option-article-mobile-"]',
      this.articleResultLimit
    );
    trimItems(
      'li[id^="predictive-search-option-product-"]',
      this.predictiveResourceLimit
    );
  }

  mergeCollectionResults(
    resultsRoot,
    localMatches,
    searchTerm,
    collectionIndex
  ) {
    const nativeCollectionItems = Array.from(
      resultsRoot.querySelectorAll(
        'li[id^="predictive-search-option-collection-"]'
      )
    );

    const collectionResults = [];
    const resultsByHandle = new Map();

    nativeCollectionItems.forEach((listItem) => {
      const anchor = listItem.querySelector('a[href]');
      const title =
        listItem.querySelector('.predictive-search__item-heading')?.textContent?.trim() ||
        anchor?.textContent?.trim() ||
        '';
      const handle = this.getCollectionHandle(anchor?.getAttribute('href'));

      const nativeResult = {
        handle,
        title,
        score: Math.max(this.scoreCollectionTitleMatch(title, searchTerm), 675),
        sourcePriority: 2,
        listItem,
        isNative: true,
        isFallback: false,
      };

      if (handle) {
        resultsByHandle.set(handle, nativeResult);
      } else {
        collectionResults.push(nativeResult);
      }
    });

    localMatches.forEach((match) => {
      const handle = String(match.collection.handle || '')
        .trim()
        .toLowerCase();

      if (!handle) return;

      const existingResult = resultsByHandle.get(handle);

      if (existingResult) {
        existingResult.score = Math.max(existingResult.score, match.score);
        return;
      }

      resultsByHandle.set(handle, {
        handle,
        title:
          String(match.collection.menuTitle || '').trim() ||
          String(match.collection.title || '').trim(),
        score: match.score,
        sourcePriority: 2,
        listItem: this.createLocalCollectionListItem(match.collection),
        isNative: false,
        isFallback: false,
      });
    });

    const directCollectionCount =
      resultsByHandle.size + collectionResults.length;

    if (directCollectionCount < this.collectionFallbackTriggerCount) {
      const remainingCollectionSlots = Math.max(
        0,
        this.collectionResultLimit - directCollectionCount
      );
      const fallbackLimit = Math.min(
        this.collectionFallbackResultLimit,
        remainingCollectionSlots
      );

      const fallbackMatches = this.getPredictiveProductBackedCollectionMatches(
        resultsRoot,
        collectionIndex,
        resultsByHandle,
        fallbackLimit
      );

      fallbackMatches.forEach((match) => {
        const handle = String(match.collection.handle || '')
          .trim()
          .toLowerCase();

        if (!handle || resultsByHandle.has(handle)) return;

        resultsByHandle.set(handle, {
          handle,
          title:
            String(match.collection.menuTitle || '').trim() ||
            String(match.collection.title || '').trim(),
          score: match.score,
          sourcePriority: 1,
          listItem: this.createLocalCollectionListItem(match.collection),
          isNative: false,
          isFallback: true,
        });
      });
    }

    resultsByHandle.forEach((result) => {
      collectionResults.push(result);
    });

    collectionResults.sort((firstResult, secondResult) => {
      if (firstResult.sourcePriority !== secondResult.sourcePriority) {
        return secondResult.sourcePriority - firstResult.sourcePriority;
      }

      if (firstResult.score !== secondResult.score) {
        return secondResult.score - firstResult.score;
      }

      if (firstResult.isNative !== secondResult.isNative) {
        return firstResult.isNative ? -1 : 1;
      }

      return firstResult.title.localeCompare(secondResult.title);
    });

    const visibleCollectionResults = collectionResults.slice(
      0,
      this.collectionResultLimit
    );

    nativeCollectionItems.forEach((listItem) => listItem.remove());

    this.removeEmptySuggestionBlock(resultsRoot);

    if (!visibleCollectionResults.length) {
      this.removeCollectionBlock(resultsRoot);
      this.refreshResultsWrapperClasses(resultsRoot);
      return;
    }

    const collectionBlock = this.getOrCreateCollectionBlock(resultsRoot);
    const collectionList = collectionBlock.querySelector(
      '[data-predictive-search-collections-list]'
    );

    collectionList.innerHTML = '';

    visibleCollectionResults.forEach((result, index) => {
      result.listItem.id =
        `predictive-search-option-collection-combined-${index + 1}`;
      collectionList.appendChild(result.listItem);
    });

    this.refreshResultsWrapperClasses(resultsRoot);
  }

  getPredictiveProductBackedCollectionMatches(
    resultsRoot,
    collectionIndex,
    excludedResultsByHandle,
    limit
  ) {
    if (
      !limit ||
      !Array.isArray(collectionIndex) ||
      !collectionIndex.length
    ) {
      return [];
    }

    const resultCollectionEntries =
      this.getPredictiveProductCollectionEntries(resultsRoot);

    if (!resultCollectionEntries.length) {
      return [];
    }

    const eligibleCollections =
      this.buildEligiblePredictiveCollectionMap(collectionIndex);
    const evidenceByHandle = new Map();

    resultCollectionEntries.forEach((entry) => {
      const handle = String(entry?.handle || '')
        .trim()
        .toLowerCase();

      if (
        !handle ||
        !eligibleCollections.has(handle) ||
        excludedResultsByHandle.has(handle)
      ) {
        return;
      }

      const productPosition = Math.max(
        1,
        Number.parseInt(entry?.productPosition, 10) || 999
      );
      const existingEvidence = evidenceByHandle.get(handle) || {
        productPositions: new Set(),
        bestProductPosition: productPosition,
      };

      existingEvidence.productPositions.add(productPosition);
      existingEvidence.bestProductPosition = Math.min(
        existingEvidence.bestProductPosition,
        productPosition
      );

      evidenceByHandle.set(handle, existingEvidence);
    });

    return Array.from(evidenceByHandle.entries())
      .map(([handle, evidence]) => {
        const collection = eligibleCollections.get(handle);
        const frequency = evidence.productPositions.size;
        const depth = this.getPredictiveCollectionDepth(collection);
        const positionScore = Math.max(
          0,
          100 - (evidence.bestProductPosition - 1) * 10
        );

        return {
          collection,
          frequency,
          bestProductPosition: evidence.bestProductPosition,
          depth,
          score: frequency * 100 + positionScore + depth * 10,
        };
      })
      .sort((firstMatch, secondMatch) => {
        if (firstMatch.frequency !== secondMatch.frequency) {
          return secondMatch.frequency - firstMatch.frequency;
        }

        if (
          firstMatch.bestProductPosition !==
          secondMatch.bestProductPosition
        ) {
          return (
            firstMatch.bestProductPosition -
            secondMatch.bestProductPosition
          );
        }

        if (firstMatch.depth !== secondMatch.depth) {
          return secondMatch.depth - firstMatch.depth;
        }

        const firstTitle =
          firstMatch.collection.menuTitle ||
          firstMatch.collection.title ||
          '';
        const secondTitle =
          secondMatch.collection.menuTitle ||
          secondMatch.collection.title ||
          '';

        return firstTitle.localeCompare(secondTitle);
      })
      .slice(0, limit);
  }

  getPredictiveProductCollectionEntries(resultsRoot) {
    const jsonElement = resultsRoot.querySelector(
      '[data-predictive-search-product-collections-json]'
    );

    if (!jsonElement) {
      return [];
    }

    try {
      const entries = JSON.parse(jsonElement.textContent || '[]');
      return Array.isArray(entries) ? entries : [];
    } catch (error) {
      console.warn(
        'Predictive product collection membership JSON could not be parsed.',
        error
      );
      return [];
    }
  }

  buildEligiblePredictiveCollectionMap(collectionIndex) {
    const eligibleCollections = new Map();

    collectionIndex.forEach((collection) => {
      const handle = String(collection?.handle || '')
        .trim()
        .toLowerCase();

      if (!handle) return;

      const existingCollection = eligibleCollections.get(handle);

      if (
        !existingCollection ||
        this.getPredictiveCollectionDepth(collection) >
          this.getPredictiveCollectionDepth(existingCollection)
      ) {
        eligibleCollections.set(handle, collection);
      }
    });

    return eligibleCollections;
  }

  getPredictiveCollectionDepth(collection) {
    return Math.max(
      0,
      String(collection?.path || '')
        .split('>')
        .filter((part) => part.trim()).length - 1
    );
  }

  removeEmptySuggestionBlock(resultsRoot) {
    const queryList = resultsRoot.querySelector(
      '#predictive-search-results-queries-list'
    );

    if (!queryList) return;

    const remainingItems = Array.from(queryList.children).filter(
      (child) => child.matches('li')
    );

    if (remainingItems.length > 0) return;

    queryList.closest('div')?.remove();
  }

  removeCollectionBlock(resultsRoot) {
    resultsRoot.querySelector('[data-predictive-search-collections]')?.remove();

    const firstGroup = this.getFirstResultGroup(resultsRoot);
    if (firstGroup && !firstGroup.children.length) {
      firstGroup.remove();
    }
  }

  getOrCreateCollectionBlock(resultsRoot) {
    let collectionBlock = resultsRoot.querySelector(
      '[data-predictive-search-collections]'
    );

    if (collectionBlock) return collectionBlock;

    const firstGroup = this.getOrCreateFirstResultGroup(resultsRoot);

    collectionBlock = document.createElement('div');
    collectionBlock.setAttribute('data-predictive-search-collections', '');
    collectionBlock.innerHTML = `
      <h2
        id="predictive-search-collections"
        class="predictive-search__heading text-body caption-with-letter-spacing"
      >
        Collections
      </h2>
      <ul
        class="predictive-search__results-list list-unstyled"
        role="group"
        aria-labelledby="predictive-search-collections"
        data-predictive-search-collections-list
      ></ul>
    `;

    const pagesWrapper = firstGroup.querySelector(
      '.predictive-search__pages-wrapper'
    );

    if (pagesWrapper) {
      firstGroup.insertBefore(collectionBlock, pagesWrapper);
    } else {
      firstGroup.appendChild(collectionBlock);
    }

    return collectionBlock;
  }

  getOrCreateFirstResultGroup(resultsRoot) {
    const wrapper = this.getOrCreateResultsWrapper(resultsRoot);
    let firstGroup = this.getFirstResultGroup(resultsRoot);

    if (firstGroup) return firstGroup;

    firstGroup = document.createElement('div');
    firstGroup.className = 'predictive-search__result-group';

    const productGroup = this.getProductResultGroup(resultsRoot);

    if (productGroup) {
      wrapper.insertBefore(firstGroup, productGroup);
    } else {
      wrapper.appendChild(firstGroup);
    }

    return firstGroup;
  }

  getFirstResultGroup(resultsRoot) {
    const wrapper = resultsRoot.querySelector(
      '#predictive-search-results-groups-wrapper'
    );

    if (!wrapper) return null;

    const productGroup = this.getProductResultGroup(resultsRoot);

    return (
      Array.from(wrapper.children).find(
        (child) =>
          child.classList?.contains('predictive-search__result-group') &&
          child !== productGroup
      ) || null
    );
  }

  getProductResultGroup(resultsRoot) {
    return (
      resultsRoot
        .querySelector('#predictive-search-products')
        ?.closest('.predictive-search__result-group') || null
    );
  }

  getOrCreateResultsWrapper(resultsRoot) {
    let wrapper = resultsRoot.querySelector(
      '#predictive-search-results-groups-wrapper'
    );

    if (wrapper) return wrapper;

    wrapper = document.createElement('div');
    wrapper.id = 'predictive-search-results-groups-wrapper';
    wrapper.className = 'predictive-search__results-groups-wrapper';

    const loadingSpinner = resultsRoot.querySelector(
      '.predictive-search__loading-state'
    );
    const searchForButton = resultsRoot.querySelector(
      '#predictive-search-option-search-keywords'
    );
    const insertionPoint = loadingSpinner || searchForButton;

    if (insertionPoint) {
      resultsRoot.insertBefore(wrapper, insertionPoint);
    } else {
      resultsRoot.prepend(wrapper);
    }

    return wrapper;
  }

  refreshResultsWrapperClasses(resultsRoot) {
    const wrapper = resultsRoot.querySelector(
      '#predictive-search-results-groups-wrapper'
    );

    if (!wrapper) return;

    const hasProducts = Boolean(
      resultsRoot.querySelector('#predictive-search-results-products-list li')
    );
    const hasSuggestions = Boolean(
      resultsRoot.querySelector('#predictive-search-results-queries-list li') ||
        resultsRoot.querySelector(
          '[data-predictive-search-collections-list] li'
        )
    );

    wrapper.classList.toggle(
      'predictive-search__results-groups-wrapper--no-products',
      !hasProducts
    );
    wrapper.classList.toggle(
      'predictive-search__results-groups-wrapper--no-suggestions',
      !hasSuggestions
    );

    const hasAnyResultGroup = Boolean(
      wrapper.querySelector('.predictive-search__result-group')
    );

    if (!hasAnyResultGroup) {
      wrapper.remove();
    }
  }

  createLocalCollectionListItem(collection) {
    const listItem = document.createElement('li');
    listItem.className = 'predictive-search__list-item';
    listItem.setAttribute('role', 'option');
    listItem.setAttribute('aria-selected', 'false');

    const anchor = document.createElement('a');
    anchor.href = collection.url;
    anchor.className = 'predictive-search__item link link--text';
    anchor.tabIndex = -1;

    const content = document.createElement('div');
    content.className =
      'predictive-search__item-content predictive-search__item-content--centered';

    const heading = document.createElement('p');
    heading.className = 'predictive-search__item-heading h5';
    heading.textContent =
      String(collection.menuTitle || '').trim() ||
      String(collection.title || '').trim();

    content.appendChild(heading);
    anchor.appendChild(content);
    listItem.appendChild(anchor);

    return listItem;
  }

  updateLiveRegionCount(resultsRoot) {
    const liveRegionCount = resultsRoot.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (!liveRegionCount) return;

    const queryCount = resultsRoot.querySelectorAll(
      '#predictive-search-results-queries-list li'
    ).length;
    const collectionCount = resultsRoot.querySelectorAll(
      '[data-predictive-search-collections-list] li'
    ).length;
    const productCount = resultsRoot.querySelectorAll(
      '#predictive-search-results-products-list li'
    ).length;
    const pageAndArticleCount = resultsRoot.querySelectorAll(
      '#predictive-search-results-pages-list-desktop li'
    ).length;

    const totalCount =
      queryCount + collectionCount + productCount + pageAndArticleCount;

    liveRegionCount.textContent =
      totalCount === 1
        ? '1 search result available.'
        : `${totalCount} search results available.`;
  }

  getLocalCollectionMatches(collectionIndex, query) {
    if (!Array.isArray(collectionIndex) || !collectionIndex.length) {
      return [];
    }

    const matchesByHandle = new Map();

    collectionIndex.forEach((collection) => {
      const handle = String(collection?.handle || '').trim();

      if (!handle) return;

      const titleScore = this.scoreCollectionTitleMatch(
        collection.title,
        query
      );
      const menuTitleScore = this.scoreCollectionTitleMatch(
        collection.menuTitle,
        query
      );
      const aliasScore = this.getCollectionAliasScore(collection, query);
      const pathScore = this.scoreTextMatch(collection.path, query);
      const score = Math.max(
        titleScore,
        menuTitleScore,
        aliasScore,
        pathScore >= 0 ? pathScore - 40 : -1
      );

      if (score < 0) return;

      const depth = Math.max(
        0,
        String(collection.path || '')
          .split('>')
          .filter((part) => part.trim()).length - 1
      );
      const existingMatch = matchesByHandle.get(handle);
      const currentMatch = {
        collection,
        score,
        depth,
      };

      if (
        !existingMatch ||
        currentMatch.score > existingMatch.score ||
        (currentMatch.score === existingMatch.score &&
          currentMatch.depth > existingMatch.depth)
      ) {
        matchesByHandle.set(handle, currentMatch);
      }
    });

    return Array.from(matchesByHandle.values()).sort(
      (firstMatch, secondMatch) => {
        if (firstMatch.score !== secondMatch.score) {
          return secondMatch.score - firstMatch.score;
        }

        if (firstMatch.depth !== secondMatch.depth) {
          return secondMatch.depth - firstMatch.depth;
        }

        const firstTitle =
          firstMatch.collection.menuTitle ||
          firstMatch.collection.title ||
          '';
        const secondTitle =
          secondMatch.collection.menuTitle ||
          secondMatch.collection.title ||
          '';

        return firstTitle.localeCompare(secondTitle);
      }
    );
  }

  normalize(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  scoreTextMatch(text, query) {
    const normalizedText = this.normalize(text);
    const normalizedQuery = this.normalize(query);

    if (!normalizedText || !normalizedQuery) {
      return -1;
    }

    if (normalizedText === normalizedQuery) {
      return 1000;
    }

    if (normalizedText.indexOf(normalizedQuery) === 0) {
      return 800;
    }

    if (normalizedText.indexOf(` ${normalizedQuery}`) !== -1) {
      return 650;
    }

    if (normalizedText.indexOf(normalizedQuery) !== -1) {
      return 500;
    }

    const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean);
    const matchingTerms = queryTerms.filter((term) =>
      normalizedText.includes(term)
    );

    if (
      matchingTerms.length === queryTerms.length &&
      queryTerms.length > 0
    ) {
      return 350 + matchingTerms.length;
    }

    return -1;
  }

  normalizeCollectionMatchTerm(term) {
    const normalizedTerm = String(term == null ? '' : term);

    if (normalizedTerm.length <= 3) {
      return normalizedTerm;
    }

    // Conservative storefront singularization, not a full-language stemmer.
    if (
      normalizedTerm.length > 4 &&
      normalizedTerm.slice(-3) === 'ies'
    ) {
      return `${normalizedTerm.slice(0, -3)}y`;
    }

    if (
      normalizedTerm.length > 5 &&
      /(ches|shes|xes|zes|sses)$/.test(normalizedTerm)
    ) {
      return normalizedTerm.slice(0, -2);
    }

    if (
      normalizedTerm.length > 3 &&
      normalizedTerm.slice(-1) === 's' &&
      !/(ss|us|is)$/.test(normalizedTerm)
    ) {
      return normalizedTerm.slice(0, -1);
    }

    return normalizedTerm;
  }

  getCollectionMatchTerms(value) {
    const ignoredTerms = new Set([
      'a',
      'an',
      'and',
      'for',
      'of',
      'or',
      'the',
      'to',
      'with',
    ]);

    return this.normalize(value)
      .split(/\s+/)
      .filter(Boolean)
      .filter((term) => !ignoredTerms.has(term))
      .map((term) => this.normalizeCollectionMatchTerm(term));
  }

  scoreCollectionTitleMatch(text, query) {
    const directScore = this.scoreTextMatch(text, query);
    const titleTerms = this.getCollectionMatchTerms(text);
    const queryTerms = this.getCollectionMatchTerms(query);

    if (!titleTerms.length || !queryTerms.length) {
      return directScore;
    }

    const titleTermSet = new Set(titleTerms);
    const queryTermSet = new Set(queryTerms);
    const canonicalTitle = titleTerms.join(' ');
    const canonicalQuery = queryTerms.join(' ');

    if (canonicalTitle === canonicalQuery) {
      return Math.max(directScore, 990);
    }

    const allTitleTermsAppearInQuery = titleTerms.every((term) =>
      queryTermSet.has(term)
    );

    if (allTitleTermsAppearInQuery) {
      const reverseTitleScore =
        titleTerms.length === 1
          ? 950
          : 900 + Math.min(titleTerms.length, 20);

      return Math.max(directScore, reverseTitleScore);
    }

    const allQueryTermsAppearInTitle = queryTerms.every((term) =>
      titleTermSet.has(term)
    );

    if (allQueryTermsAppearInTitle) {
      return Math.max(
        directScore,
        700 + Math.min(queryTerms.length, 20)
      );
    }

    return directScore;
  }

  scoreCollectionAliasMatch(alias, query) {
    const aliasScore = this.scoreCollectionTitleMatch(alias, query);

    if (aliasScore < 0) {
      return -1;
    }

    if (aliasScore >= 990) {
      return 940;
    }

    if (aliasScore >= 900) {
      return 900;
    }

    if (aliasScore >= 700) {
      return 760;
    }

    return Math.min(aliasScore, 650);
  }

  getCollectionAliasScore(collection, query) {
    if (
      !collection ||
      !Array.isArray(collection.aliases) ||
      !collection.aliases.length
    ) {
      return -1;
    }

    return collection.aliases.reduce(
      (bestScore, searchAlias) =>
        Math.max(
          bestScore,
          this.scoreCollectionAliasMatch(searchAlias, query)
        ),
      -1
    );
  }

  getCollectionHandle(url) {
    if (!url) return '';

    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/collections\/([^/?#]+)/i);

      return match ? decodeURIComponent(match[1]).toLowerCase() : '';
    } catch (error) {
      return '';
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const liveRegionCount = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    this.setLiveRegionText(liveRegionCount?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('.section-header')?.getBoundingClientRect().bottom;

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
